import { type ChangeEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { deleteProduct, listProducts } from '../../api/stock'
import { getMyPractices } from '../../api/staff'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { inputClass, selectClass } from '../../components/ui/FormField'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass } from '@/lib/tableStyles'
import type { ProductListItem } from '../../types/stock'

function ScheduleBadge({ schedule }: { schedule: number | null }) {
  const scheduled = schedule !== null
  return (
    <span className={`inline-block py-[0.15rem] px-[0.55rem] rounded-full text-xs font-semibold ${scheduled ? 'bg-accent-muted text-accent' : 'bg-surface-alt text-text-3'}`}>
      {scheduled ? `Schedule ${schedule}` : 'Unscheduled'}
    </span>
  )
}

export default function ProductList() {
  const toast = useToast()
  const confirm = useConfirm()

  const [search, setSearch] = useState('')
  const [schedule, setSchedule] = useState<string>('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [deactivating, setDeactivating] = useState<number | null>(null)

  const { data: practices, loading: practicesLoading, error: practicesError } = useFetch(
    () => getMyPractices(),
    [],
  )

  const practiceId = practices?.[0]?.id ?? null

  const { data: products, loading, error, refetch } = useFetch(
    () => {
      if (!practiceId) return Promise.resolve([] as ProductListItem[])
      return listProducts({
        practice_id: practiceId,
        search: search.trim() || undefined,
        schedule: schedule !== '' ? Number(schedule) : undefined,
        is_active: activeOnly ? true : undefined,
      })
    },
    [practiceId, search, schedule, activeOnly],
  )

  const handleDeactivate = async (product: ProductListItem) => {
    const ok = await confirm({
      title: 'Deactivate product',
      message: `Deactivate "${product.name}"? It will no longer appear in stock lists.`,
      confirmLabel: 'Deactivate',
      danger: true,
    })
    if (!ok) return
    setDeactivating(product.id)
    try {
      await deleteProduct(product.id)
      toast.success(`"${product.name}" deactivated.`)
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to deactivate product.')
    } finally {
      setDeactivating(null)
    }
  }

  if (practicesLoading) return <div className="text-text-2 p-8">Loading…</div>
  if (practicesError) return <div className="p-8"><ErrorBox>{practicesError}</ErrorBox></div>
  if (!practices || practices.length === 0) {
    return <div className="p-8"><ErrorBox>No practices found. Please set up a practice first.</ErrorBox></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Products</h1>
        <ButtonLink to="/stock/products/new" size="md">+ Add Product</ButtonLink>
      </div>

      <Card className="mb-6 p-4">
        <div className="flex gap-3 flex-wrap items-center">
          <input
            type="search"
            placeholder="Search products…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            className={`${inputClass()} w-[220px]`}
          />
          <select
            value={schedule}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => setSchedule(e.target.value)}
            className={`${selectClass()} w-[170px]`}
          >
            <option value="">All schedules</option>
            <option value="0">Unscheduled</option>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={String(n)}>Schedule {n}</option>
            ))}
          </select>
          <label className="flex items-center gap-[0.4rem] text-sm text-text cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setActiveOnly(e.target.checked)}
            />
            Active only
          </label>
        </div>
      </Card>

      <Card>
        <SectionTitle>
          {practices[0].name} — Products
          {products && <span className="font-normal ml-2 text-text-3">({products.length})</span>}
        </SectionTitle>

        {error && <ErrorBox>{error}</ErrorBox>}

        {loading ? (
          <div className="text-text-3 py-6">Loading products…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Schedule</th>
                  <th className={thClass}>Barcode</th>
                  <th className={thClass}>Unit</th>
                  <th className={thClass}>On Hand</th>
                  <th className={thClass}>Actions</th>
                </tr>
              </thead>
              <tbody className={tbodyHoverClass}>
                {products && products.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`${tdClass} text-text-3 text-center py-8`}>
                      No products found.
                    </td>
                  </tr>
                ) : (
                  products?.map((p) => (
                    <tr key={p.id}>
                      <td className={tdClass}>
                        <div className="font-semibold">{p.name}</div>
                        {p.generic_name && <div className="text-[0.8rem] text-text-3">{p.generic_name}</div>}
                      </td>
                      <td className={tdClass}><ScheduleBadge schedule={p.schedule} /></td>
                      <td className={`${tdClass} text-text-3 font-mono text-[0.82rem]`}>{p.barcode ?? '—'}</td>
                      <td className={tdClass}>{p.unit}</td>
                      <td className={`${tdClass} font-semibold`}>{p.total_on_hand}</td>
                      <td className={tdClass}>
                        <div className="flex gap-2 items-center">
                          <Link
                            to={`/stock/products/${p.id}/edit`}
                            className="text-accent text-[0.82rem] font-medium no-underline"
                          >
                            Edit
                          </Link>
                          {p.is_active && (
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={deactivating === p.id}
                              onClick={() => void handleDeactivate(p)}
                            >
                              {deactivating === p.id ? 'Deactivating…' : 'Deactivate'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
